/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import type { ReactNode, MutableRefObject } from 'react';
import type {
  ClaraRecordingStatus,
  ClaraUploadState,
  RecordingEngineType,
  RecordingProgress,
  RecordingProgressStage,
} from '@clarix/shared-types';
import type { AudioWorkletRecorder } from './audio-worklet-recorder';
import type { LiveInput } from './mic-input';
import type { RecordingInputEvent } from './types';
import type { RecordingDB } from './recording-db';
import type { ChunkUploadQueue } from './chunk-upload-queue';
import { isConnectivityLost, subscribeConnectivity } from '@clarix/utils';

// ── Engine refs (survive component unmounts) ─────────────────────────────────

export interface RecordingEngineRefs {
  statusRef: MutableRefObject<ClaraRecordingStatus>;
  uploadStateRef: MutableRefObject<ClaraUploadState | null>;
  autoStopTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  segmentStartTimeRef: MutableRefObject<number>;
  maxDurationMsRef: MutableRefObject<number>;
  remainingMsRef: MutableRefObject<number>;
  autoStoppedRef: MutableRefObject<boolean>;
  stopAtTimeRef: MutableRefObject<number>;
  isStoppingRef: MutableRefObject<boolean>;
  triggerAutoStopRef: MutableRefObject<(() => void) | null>;
  engineTypeRef: MutableRefObject<RecordingEngineType | null>;
  // AudioWorklet engine
  workletRecorderRef: MutableRefObject<AudioWorkletRecorder | null>;
  recordingDbRef: MutableRefObject<RecordingDB | null>;
  uploadQueueRef: MutableRefObject<ChunkUploadQueue | null>;
  wavAccRef: MutableRefObject<Blob[]>;
  wavAccSizeRef: MutableRefObject<number>;
  s3PartNumberRef: MutableRefObject<number>;
  pendingChunkRef: MutableRefObject<Promise<void>>;
  // MediaRecorder fallback
  mediaRecorderRef: MutableRefObject<MediaRecorder | null>;
  streamRef: MutableRefObject<MediaStream | null>;
  bufferRef: MutableRefObject<Blob[]>;
  bufferSizeRef: MutableRefObject<number>;
  isUploadingRef: MutableRefObject<boolean>;
  isFetchingMoreUrlsRef: MutableRefObject<boolean>;
  // Live mic input (both engines): swappable source + meter tap
  liveInputRef: MutableRefObject<LiveInput | null>;
  /** The fallback engine's own graph; the worklet engine owns its context. */
  inputContextRef: MutableRefObject<AudioContext | null>;
  /** One per mounted useRecordingControls, so input events outlive any single caller. */
  inputListenersRef: MutableRefObject<Set<(event: RecordingInputEvent) => void>>;
  /** Writes the audio not yet in IndexedDB; set while a take runs. `final`: the page is going away. */
  flushTailRef: MutableRefObject<((final: boolean) => void) | null>;
}

// ── Internal context value ────────────────────────────────────────────────────

export interface RecordingContextInternals {
  engineRefs: RecordingEngineRefs;

  // Observable state
  activeScopeId: string | null;
  status: ClaraRecordingStatus;
  error: string | null;
  isProcessing: boolean;
  wasAutoStopped: boolean;
  progress: RecordingProgress;
  activeJobId: number | null;

  // State setters (used by useRecordingControls and progress sink)
  setActiveScopeId: (id: string | null) => void;
  setStatus: (s: ClaraRecordingStatus) => void;
  setError: (e: string | null) => void;
  setIsProcessing: (v: boolean) => void;
  setWasAutoStopped: (v: boolean) => void;
  setActiveJobId: (id: number | null) => void;

  // Progress sink
  reportProgress: (next: {
    stage: RecordingProgressStage;
    percent: number;
    message?: string | null;
  }) => void;
  finishProgress: (result: { status: 'completed' | 'failed'; errorCode?: string | null }) => void;
  resetProgress: () => void;
}

const INITIAL_PROGRESS: RecordingProgress = {
  stage: null,
  percent: 0,
  message: null,
  errorCode: null,
};

const RecordingContext = createContext<RecordingContextInternals | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

interface RecordingProviderProps {
  children: ReactNode;
}

export function RecordingProvider({ children }: RecordingProviderProps) {
  // ── Persistent refs (survive any child unmount) ─────────────────────────
  const statusRef = useRef<ClaraRecordingStatus>('idle');
  const uploadStateRef = useRef<ClaraUploadState | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const segmentStartTimeRef = useRef(0);
  // Initialized to 0; set by useRecordingControls on start() from config.maxRecordingMinutes
  const maxDurationMsRef = useRef(0);
  const remainingMsRef = useRef(0);
  const autoStoppedRef = useRef(false);
  const stopAtTimeRef = useRef(0);
  const isStoppingRef = useRef(false);
  const triggerAutoStopRef = useRef<(() => void) | null>(null);
  const engineTypeRef = useRef<RecordingEngineType | null>(null);

  // AudioWorklet engine refs
  const workletRecorderRef = useRef<AudioWorkletRecorder | null>(null);
  const recordingDbRef = useRef<RecordingDB | null>(null);
  const uploadQueueRef = useRef<ChunkUploadQueue | null>(null);
  const wavAccRef = useRef<Blob[]>([]);
  const wavAccSizeRef = useRef(0);
  const s3PartNumberRef = useRef(1);
  const pendingChunkRef = useRef<Promise<void>>(Promise.resolve());

  // MediaRecorder fallback refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const bufferRef = useRef<Blob[]>([]);
  const bufferSizeRef = useRef(0);
  const isUploadingRef = useRef(false);
  const isFetchingMoreUrlsRef = useRef(false);

  const liveInputRef = useRef<LiveInput | null>(null);
  const inputContextRef = useRef<AudioContext | null>(null);
  const inputListenersRef = useRef(new Set<(event: RecordingInputEvent) => void>());
  const flushTailRef = useRef<((final: boolean) => void) | null>(null);

  const engineRefs: RecordingEngineRefs = {
    statusRef,
    uploadStateRef,
    autoStopTimerRef,
    segmentStartTimeRef,
    maxDurationMsRef,
    remainingMsRef,
    autoStoppedRef,
    stopAtTimeRef,
    isStoppingRef,
    triggerAutoStopRef,
    engineTypeRef,
    workletRecorderRef,
    recordingDbRef,
    uploadQueueRef,
    wavAccRef,
    wavAccSizeRef,
    s3PartNumberRef,
    pendingChunkRef,
    mediaRecorderRef,
    streamRef,
    bufferRef,
    bufferSizeRef,
    isUploadingRef,
    isFetchingMoreUrlsRef,
    liveInputRef,
    inputContextRef,
    inputListenersRef,
    flushTailRef,
  };

  // ── Observable state (triggers re-renders for consumers) ────────────────
  const [activeScopeId, setActiveScopeId] = useState<string | null>(null);
  const [status, setStatus] = useState<ClaraRecordingStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [wasAutoStopped, setWasAutoStopped] = useState(false);
  const [progress, setProgress] = useState<RecordingProgress>(INITIAL_PROGRESS);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);

  const reportProgress = useCallback(
    (next: { stage: RecordingProgressStage; percent: number; message?: string | null }) => {
      setProgress((prev) => {
        // Monotonicity guard: never let the bar ratchet backwards on reordered events.
        if (prev.percent > next.percent) return prev;
        return {
          stage: next.stage,
          percent: next.percent,
          message: next.message ?? null,
          errorCode: null,
        };
      });
    },
    [],
  );

  const finishProgress = useCallback(
    (result: { status: 'completed' | 'failed'; errorCode?: string | null }) => {
      setProgress((prev) => ({
        stage: result.status,
        percent: result.status === 'completed' ? 100 : prev.percent,
        message: null,
        errorCode: result.errorCode ?? null,
      }));
    },
    [],
  );

  const resetProgress = useCallback(() => setProgress(INITIAL_PROGRESS), []);

  // ── beforeunload: warn before tab close while recording ─────────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const s = statusRef.current;
      if (s === 'recording' || s === 'paused' || s === 'completing') {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ── visibilitychange: nudge upload queue + resume AudioContext ──────────
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    uploadQueueRef.current?.notify();
    workletRecorderRef.current?.resume();
    if (inputContextRef.current?.state === 'suspended') void inputContextRef.current.resume();
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleVisibilityChange]);

  // Parts that failed while offline sit in IndexedDB as `failed`; send them as soon as the network is back.
  useEffect(() => {
    const onOnline = () => {
      if (!isConnectivityLost()) uploadQueueRef.current?.notify();
    };
    window.addEventListener('online', onOnline);
    const unsubscribe = subscribeConnectivity(onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      unsubscribe();
    };
  }, []);

  // A closing or hidden tab writes what is still in memory, so a closed tab loses only the last seconds.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushTailRef.current?.(false);
    };
    const onPageHide = () => flushTailRef.current?.(true);
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  const value: RecordingContextInternals = {
    engineRefs,
    activeScopeId,
    status,
    error,
    isProcessing,
    wasAutoStopped,
    progress,
    activeJobId,
    setActiveScopeId,
    setStatus,
    setError,
    setIsProcessing,
    setWasAutoStopped,
    setActiveJobId,
    reportProgress,
    finishProgress,
    resetProgress,
  };

  return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>;
}

// ── Internal hook (package-private: used by useRecordingControls) ─────────────

/**
 * Package-internal access to all recording context internals.
 * Not exported from the package barrel — consumers use useRecordingState /
 * useRecordingProgressSink instead.
 */
export function _useRecordingInternals(): RecordingContextInternals {
  const ctx = useContext(RecordingContext);
  if (!ctx) throw new Error('useRecordingControls must be used within RecordingProvider');
  return ctx;
}

// ── Public hooks ──────────────────────────────────────────────────────────────

/** Read the current observable recording state for UI rendering. */
export function useRecordingState() {
  const ctx = _useRecordingInternals();
  return {
    status: ctx.status,
    isProcessing: ctx.isProcessing,
    progress: ctx.progress,
    activeJobId: ctx.activeJobId,
    wasAutoStopped: ctx.wasAutoStopped,
    activeScopeId: ctx.activeScopeId,
    error: ctx.error,
  };
}

/**
 * Setters for driving the progress UI and engine status from a WS hook or
 * manual upload hook. The WS hook stays in the consuming app; it calls these
 * to push pipeline events into the shared provider.
 */
export function useRecordingProgressSink() {
  const ctx = _useRecordingInternals();
  return {
    reportProgress: ctx.reportProgress,
    finishProgress: ctx.finishProgress,
    resetProgress: ctx.resetProgress,
    setActiveJobId: ctx.setActiveJobId,
    setIsProcessing: ctx.setIsProcessing,
    // Exposed so the app's WS hook can set the engine's terminal status
    // (e.g. 'completed' / 'error') when recording.ready fires.
    setEngineStatus: ctx.setStatus,
  };
}
